import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useNavigate, useParams } from "react-router-dom";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  getFullEmployeePhotoUrl,
  useFetchEmployeeByIdQuery,
  useUpdateEmployeeMutation,
  useUploadEmployeePhotosMutation,
} from "../../store/api/employeesApi";
import "./admin.css";
import "./AddEmployee.css";

type EmployeeForm = {
  firstName: string;
  lastName: string;
  gender: string;
  maritalStatus: string;
  email: string;
  phone: string;
  aadhaarNumber: string;
  department: string;
  role: string;
  status: string;
  joinedAt: string;
};

const steps = [
  { label: "Personal Details", icon: "bi-person-vcard" },
  { label: "Contact Details", icon: "bi-envelope-at" },
  { label: "Job Details", icon: "bi-briefcase" },
  { label: "Review", icon: "bi-check2-circle" },
];

const departments = [
  "Civil", "Site Execution", "MEP", "Interior",
  "Procurement", "Planning", "Operations", "Admin",
];

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isValidPhone = (v: string) => {
  const d = v.replace(/\D/g, "");
  return d.length >= 10 && d.length <= 13;
};
const isValidAadhaar = (v: string) => v === "" || /^\d{12}$/.test(v.replace(/\s/g, ""));

const statusBadgeClass = (s: string) =>
  s === "active" ? "text-bg-success" : s === "on-leave" ? "text-bg-warning" : "text-bg-secondary";

interface PhotoUploadProps {
  id: string;
  label: string;
  hint: string;
  existingUrl?: string | null;
  file: File | null;
  onChange: (f: File | null) => void;
  blurPreview?: boolean;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  id, label, hint, existingUrl, file, onChange, blurPreview,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const newPreview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const preview = newPreview ?? existingUrl ?? null;

  const handleFile = useCallback((f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return;
    if (f.size > 5_000_000) {
      alert("File must be under 5 MB.");
      return;
    }
    onChange(f);
  }, [onChange]);

  return (
    <div className="emp-photo-field">
      <div className="emp-photo-label-row">
        <label htmlFor={id} className="emp-photo-label">{label}</label>
        {file && (
          <button type="button" className="emp-photo-clear" onClick={() => onChange(null)}>
            <i className="bi bi-x-circle" /> Remove new
          </button>
        )}
      </div>

      <div
        className={`emp-photo-drop${dragOver ? " drag-over" : ""}${preview ? " has-file" : ""}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0] ?? null); }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        aria-label={`Upload ${label}`}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt={label}
              className="emp-photo-preview"
              style={{ filter: blurPreview && !hover ? "blur(8px)" : "none" }}
            />
            {hover && (
              <div className="emp-photo-overlay">
                <i className="bi bi-camera-fill" />
                <span>Change Photo</span>
              </div>
            )}
          </>
        ) : (
          <div className="emp-photo-placeholder">
            <i className="bi bi-cloud-arrow-up" />
            <span>Click or drag &amp; drop</span>
            <small>JPG, PNG or WebP · max 5 MB</small>
          </div>
        )}
      </div>

      {existingUrl && !file ? (
        <p className="emp-photo-hint" style={{ color: "#10b981" }}>
          <i className="bi bi-check-circle" /> Existing photo available - click to replace
        </p>
      ) : null}
      {!existingUrl && !file ? <p className="emp-photo-hint">{hint}</p> : null}
      {file ? (
        <p className="emp-photo-hint" style={{ color: "#f59e0b" }}>
          New file selected - changes will be saved on update
        </p>
      ) : null}

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/*"
        title={label}
        aria-label={`Upload ${label}`}
        className="emp-photo-input"
        onChange={(e) => { handleFile(e.target.files?.[0] ?? null); e.target.value = ""; }}
      />
    </div>
  );
};

const EditEmployee: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const empId = Number(id);
  const navigate = useNavigate();

  const { data: emp, isLoading, isError } = useFetchEmployeeByIdQuery(empId, {
    skip: !empId || Number.isNaN(empId),
  });
  const [updateEmployee, { isLoading: isUpdating }] = useUpdateEmployeeMutation();
  const [uploadPhotos, { isLoading: isUploading }] = useUploadEmployeePhotosMutation();

  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState<EmployeeForm>({
    firstName: "",
    lastName: "",
    gender: "",
    maritalStatus: "",
    email: "",
    phone: "",
    aadhaarNumber: "",
    department: "",
    role: "",
    status: "active",
    joinedAt: "",
  });
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [aadhaarPhoto, setAadhaarPhoto] = useState<File | null>(null);
  const [touched, setTouched] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  useEffect(() => {
    if (!emp) return;
    setForm({
      firstName: emp.firstName ?? "",
      lastName: emp.lastName ?? "",
      gender: emp.gender ?? "",
      maritalStatus: emp.maritalStatus ?? "",
      email: emp.email ?? "",
      phone: emp.phone ?? "",
      aadhaarNumber: emp.aadhaarNumber ?? "",
      department: emp.department ?? "",
      role: emp.role ?? "",
      status: emp.status ?? "active",
      joinedAt: emp.joinedAt ?? "",
    });
  }, [emp]);

  const existingProfileUrl = getFullEmployeePhotoUrl(emp?.profilePhotoUrl ?? null);
  const existingAadhaarUrl = getFullEmployeePhotoUrl(emp?.aadhaarPhotoUrl ?? null);

  const isSubmitting = isUpdating || isUploading;

  const updateField = (field: keyof EmployeeForm, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const stepValid = useMemo(() => {
    switch (currentStep) {
      case 0:
        return form.firstName.trim().length > 0 && form.lastName.trim().length > 0;
      case 1:
        return (
          isValidEmail(form.email) &&
          isValidPhone(form.phone) &&
          isValidAadhaar(form.aadhaarNumber)
        );
      case 2:
        return form.department.trim().length > 0 && form.role.trim().length > 0;
      default:
        return true;
    }
  }, [currentStep, form]);

  const handleNext = () => {
    setTouched(true);
    if (stepValid) {
      setCurrentStep((prev) => Math.min(steps.length - 1, prev + 1));
      setTouched(false);
    }
  };

  const handlePrevious = () => {
    setSubmitError("");
    setSubmitSuccess("");
    setTouched(false);
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const handleStepJump = (index: number) => {
    if (index <= currentStep) {
      setTouched(false);
      setCurrentStep(index);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");
    setTouched(true);
    if (currentStep !== steps.length - 1) return;
    if (!stepValid || !emp) return;

    try {
      await updateEmployee({
        id: emp.id,
        payload: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          gender: form.gender || null,
          maritalStatus: form.maritalStatus || null,
          email: form.email.trim(),
          phone: form.phone.trim(),
          department: form.department.trim(),
          role: form.role.trim(),
          status: form.status || "active",
          joinedAt: form.joinedAt || null,
          aadhaarNumber: form.aadhaarNumber.replace(/\s/g, "") || null,
        },
      }).unwrap();

      if (profilePhoto || aadhaarPhoto) {
        await uploadPhotos({ id: emp.id, profilePhoto, aadhaarPhoto }).unwrap();
      }

      setSubmitSuccess("Employee updated successfully.");
      setProfilePhoto(null);
      setAadhaarPhoto(null);
      setTouched(false);
      window.setTimeout(() => navigate("/admin/employees"), 1200);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const initials = `${form.firstName.charAt(0)}${form.lastName.charAt(0)}`.toUpperCase().trim();
  const progressPercent = ((currentStep + 1) / steps.length) * 100;

  if (isLoading) {
    return (
      <section className="admin-page">
        <div className="dash-empty-state">
          <span className="spinner-border text-secondary" role="status" />
          <span>Loading employee...</span>
        </div>
      </section>
    );
  }

  if (isError || !emp) {
    return (
      <section className="admin-page">
        <div className="dash-empty-state" style={{ color: "#e11d48" }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "2rem" }} />
          <span>Employee not found.</span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mt-2"
            onClick={() => navigate("/admin/employees")}
          >
            Back to Employees
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <div className="container-fluid px-0">
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
          <div style={{ background: "linear-gradient(135deg, #78350f, #d97706, #f59e0b)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-pencil-square" style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>
                  Edit Employee
                </div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>
                  Update {emp.firstName} {emp.lastName} in {steps.length} steps
                </div>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 600 }}>
                Step {currentStep + 1} of {steps.length}
              </span>
              <button
                type="button"
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 8, padding: "4px 12px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
                onClick={() => navigate("/admin/employees")}
              >
                <i className="bi bi-arrow-left" /> Back
              </button>
            </div>
          </div>

          <div className="card-body p-4 p-md-5">
            {submitSuccess && (
              <div className="alert alert-success d-flex align-items-center gap-2 mb-4">
                <i className="bi bi-check-circle-fill" /><span>{submitSuccess}</span>
              </div>
            )}
            {submitError && (
              <div className="alert alert-danger d-flex align-items-center gap-2 mb-4">
                <i className="bi bi-exclamation-triangle-fill" /><span>{submitError}</span>
              </div>
            )}

            <div className="employee-stepper mb-4">
              <div className="d-flex flex-wrap gap-2 mb-3">
                {steps.map((step, index) => {
                  const isActive = index === currentStep;
                  const isComplete = index < currentStep;
                  const isClickable = index <= currentStep;
                  return (
                    <button
                      key={step.label}
                      type="button"
                      className={`employee-step-pill flex-fill${isActive ? " active" : ""}${isComplete ? " complete" : ""}`}
                      onClick={() => handleStepJump(index)}
                      disabled={!isClickable}
                    >
                      <span className="employee-step-pill-index">
                        {isComplete ? <i className="bi bi-check-lg" /> : index + 1}
                      </span>
                      <span className="employee-step-pill-content">
                        <i className={`bi ${step.icon} me-2`} />{step.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="progress employee-step-progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${progressPercent}%`, background: "linear-gradient(135deg, #d97706, #f59e0b)" }}
                >
                  {Math.round(progressPercent)}%
                </div>
              </div>
            </div>

            <form noValidate className={touched ? "was-validated" : ""} onSubmit={handleSubmit}>
              {currentStep === 0 && (
                <div className="row g-4">
                  <div className="col-md-3">
                    <PhotoUpload
                      id="edit-profile-photo"
                      label="Profile Photo"
                      hint="Clear face photo. Optional."
                      existingUrl={existingProfileUrl}
                      file={profilePhoto}
                      onChange={setProfilePhoto}
                    />
                  </div>

                  <div className="col-md-9">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div style={{ borderLeft: "3px solid #6366f1", paddingLeft: 10 }}>
                          <label className="form-label fw-semibold" htmlFor="edit-first-name">
                            First Name <span className="text-danger">*</span>
                            <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Employee's legal first name</small>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text" style={{ background: "#eef2ff", border: "1.5px solid #c7d2fe", color: "#4f46e5" }}>
                              <i className="bi bi-person-fill" />
                            </span>
                            <input
                              id="edit-first-name"
                              type="text"
                              className="form-control"
                              title="First name"
                              value={form.firstName}
                              onChange={(e) => updateField("firstName", e.target.value)}
                              required
                            />
                            <div className="invalid-feedback">Please enter the first name.</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div style={{ borderLeft: "3px solid #0ea5e9", paddingLeft: 10 }}>
                          <label className="form-label fw-semibold" htmlFor="edit-last-name">
                            Last Name <span className="text-danger">*</span>
                            <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Employee's legal last name</small>
                          </label>
                          <div className="input-group has-validation">
                            <span className="input-group-text" style={{ background: "#e0f2fe", border: "1.5px solid #7dd3fc", color: "#0284c7" }}>
                              <i className="bi bi-person-badge" />
                            </span>
                            <input
                              id="edit-last-name"
                              type="text"
                              className="form-control"
                              title="Last name"
                              value={form.lastName}
                              onChange={(e) => updateField("lastName", e.target.value)}
                              required
                            />
                            <div className="invalid-feedback">Please enter the last name.</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div style={{ borderLeft: "3px solid #ec4899", paddingLeft: 10 }}>
                          <label className="form-label fw-semibold" htmlFor="edit-gender">
                            Gender
                            <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Select the employee's gender</small>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ background: "#fdf2f8", border: "1.5px solid #f9a8d4", color: "#be185d" }}>
                              <i className="bi bi-person-hearts" />
                            </span>
                            <select
                              id="edit-gender"
                              className="form-select"
                              title="Gender"
                              value={form.gender}
                              onChange={(e) => updateField("gender", e.target.value)}
                            >
                              <option value="">Select gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div style={{ borderLeft: "3px solid #8b5cf6", paddingLeft: 10 }}>
                          <label className="form-label fw-semibold" htmlFor="edit-marital-status">
                            Marital Status
                            <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Current marital status</small>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ background: "#f5f3ff", border: "1.5px solid #c4b5fd", color: "#6d28d9" }}>
                              <i className="bi bi-heart" />
                            </span>
                            <select
                              id="edit-marital-status"
                              className="form-select"
                              title="Marital status"
                              value={form.maritalStatus}
                              onChange={(e) => updateField("maritalStatus", e.target.value)}
                            >
                              <option value="">Select marital status</option>
                              <option value="single">Single</option>
                              <option value="married">Married</option>
                              <option value="divorced">Divorced</option>
                              <option value="widowed">Widowed</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #f59e0b", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold" htmlFor="edit-email">
                        Email <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Work email for login and communication</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#fefce8", border: "1.5px solid #fde68a", color: "#b45309" }}>
                          <i className="bi bi-envelope" />
                        </span>
                        <input
                          id="edit-email"
                          type="email"
                          className={`form-control${touched && !isValidEmail(form.email) ? " is-invalid" : ""}`}
                          title="Email address"
                          value={form.email}
                          onChange={(e) => updateField("email", e.target.value)}
                          required
                        />
                        <div className="invalid-feedback">Enter a valid email address.</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #10b981", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold" htmlFor="edit-phone">
                        Phone Number <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>10-13 digit mobile, country code optional</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#d1fae5", border: "1.5px solid #6ee7b7", color: "#047857" }}>
                          <i className="bi bi-telephone" />
                        </span>
                        <input
                          id="edit-phone"
                          type="tel"
                          className={`form-control${touched && !isValidPhone(form.phone) ? " is-invalid" : ""}`}
                          title="Phone number"
                          value={form.phone}
                          onChange={(e) => updateField("phone", e.target.value)}
                          required
                        />
                        <div className="invalid-feedback">Enter a valid phone number.</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #f43f5e", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold" htmlFor="edit-aadhaar-number">
                        Aadhaar Number
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>12-digit Aadhaar card number</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#fff1f2", border: "1.5px solid #fda4af", color: "#be123c" }}>
                          <i className="bi bi-credit-card-2-front" />
                        </span>
                        <input
                          id="edit-aadhaar-number"
                          type="text"
                          inputMode="numeric"
                          maxLength={14}
                          className={`form-control${touched && !isValidAadhaar(form.aadhaarNumber) ? " is-invalid" : ""}`}
                          title="Aadhaar number"
                          placeholder="XXXX XXXX XXXX"
                          value={form.aadhaarNumber}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "").slice(0, 12);
                            updateField("aadhaarNumber", raw.replace(/(\d{4})(?=\d)/g, "$1 ").trim());
                          }}
                        />
                        <div className="invalid-feedback">Enter a valid 12-digit Aadhaar number.</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <PhotoUpload
                      id="edit-aadhaar-photo"
                      label="Aadhaar Card Photo"
                      hint="Upload a clear scan of the Aadhaar card."
                      existingUrl={existingAadhaarUrl}
                      file={aadhaarPhoto}
                      onChange={setAadhaarPhoto}
                      blurPreview
                    />
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="row g-3 align-items-end">
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #a855f7", paddingLeft: 10 }}>
                      <label htmlFor="edit-department" className="form-label fw-semibold">
                        Department <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Choose or type a department</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#f3e8ff", border: "1.5px solid #d8b4fe", color: "#7c3aed" }}>
                          <i className="bi bi-building" />
                        </span>
                        <input
                          id="edit-department"
                          list="edit-department-options"
                          className="form-control"
                          title="Department"
                          value={form.department}
                          onChange={(e) => updateField("department", e.target.value)}
                          required
                        />
                        <datalist id="edit-department-options">
                          {departments.map((dept) => <option key={dept} value={dept} />)}
                        </datalist>
                        <div className="invalid-feedback">Choose or enter a department.</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #3b82f6", paddingLeft: 10 }}>
                      <label htmlFor="edit-role" className="form-label fw-semibold">
                        Role / Position <span className="text-danger">*</span>
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Job title within the department</small>
                      </label>
                      <div className="input-group has-validation">
                        <span className="input-group-text" style={{ background: "#eff6ff", border: "1.5px solid #93c5fd", color: "#1d4ed8" }}>
                          <i className="bi bi-briefcase" />
                        </span>
                        <input
                          id="edit-role"
                          type="text"
                          className="form-control"
                          title="Role or position"
                          value={form.role}
                          onChange={(e) => updateField("role", e.target.value)}
                          required
                        />
                        <div className="invalid-feedback">Enter a job role.</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #f43f5e", paddingLeft: 10 }}>
                      <label htmlFor="edit-joined-at" className="form-label fw-semibold">
                        Date of Joining
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>When the employee joined</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#fff1f2", border: "1.5px solid #fda4af", color: "#be123c" }}>
                          <i className="bi bi-calendar-event" />
                        </span>
                        <input
                          id="edit-joined-at"
                          type="date"
                          className="form-control"
                          title="Date of joining"
                          value={form.joinedAt}
                          onChange={(e) => updateField("joinedAt", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #14b8a6", paddingLeft: 10 }}>
                      <label htmlFor="edit-status" className="form-label fw-semibold">
                        Status
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Current employment status</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#ccfbf1", border: "1.5px solid #5eead4", color: "#0f766e" }}>
                          <i className="bi bi-check-circle" />
                        </span>
                        <select
                          id="edit-status"
                          className="form-select"
                          title="Employment status"
                          aria-label="Employment status"
                          value={form.status}
                          onChange={(e) => updateField("status", e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="on-leave">On Leave</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="row g-4 align-items-stretch">
                  <div className="col-lg-4">
                    <div className="employee-summary-avatar">
                      {profilePhoto ? (
                        <img
                          src={URL.createObjectURL(profilePhoto)}
                          alt="Profile preview"
                          style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover", border: "3px solid #d97706", boxShadow: "0 4px 14px rgba(217,119,6,0.3)", marginBottom: 12 }}
                        />
                      ) : existingProfileUrl ? (
                        <img
                          src={existingProfileUrl}
                          alt="Profile preview"
                          style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover", border: "3px solid #d97706", boxShadow: "0 4px 14px rgba(217,119,6,0.3)", marginBottom: 12 }}
                        />
                      ) : (
                        <div className="employee-avatar-circle" style={{ background: "linear-gradient(135deg, #d97706, #f59e0b)" }}>
                          {initials || <i className="bi bi-person" />}
                        </div>
                      )}

                      <h3 className="h5 mb-1 mt-2">
                        {`${form.firstName} ${form.lastName}`.trim() || "Employee"}
                      </h3>
                      <p className="text-muted mb-2">{form.role || "Role pending"}</p>
                      <span className={`badge rounded-pill px-3 py-2 ${statusBadgeClass(form.status)}`}>
                        {form.status}
                      </span>

                      <div className="emp-review-doc-row mt-3">
                        <div className={`emp-review-doc-item ${(profilePhoto || existingProfileUrl) ? "ok" : "na"}`}>
                          <i className={`bi ${(profilePhoto || existingProfileUrl) ? "bi-check-circle-fill" : "bi-dash-circle"}`} />
                          Profile Photo
                        </div>
                        <div className={`emp-review-doc-item ${(aadhaarPhoto || existingAadhaarUrl) ? "ok" : "na"}`}>
                          <i className={`bi ${(aadhaarPhoto || existingAadhaarUrl) ? "bi-check-circle-fill" : "bi-dash-circle"}`} />
                          Aadhaar Card
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-8">
                    <div className="card border rounded-4 bg-light-subtle h-100">
                      <div className="card-body">
                        <h4 className="h6 mb-3 d-flex align-items-center gap-2">
                          <i className="bi bi-clipboard-check" /> Review Details
                        </h4>
                        <dl className="row mb-0 employee-summary-grid">
                          <dt className="col-sm-4">Full Name</dt>
                          <dd className="col-sm-8">{`${form.firstName} ${form.lastName}`.trim() || "-"}</dd>

                          <dt className="col-sm-4">Email</dt>
                          <dd className="col-sm-8">{form.email || "-"}</dd>

                          <dt className="col-sm-4">Gender</dt>
                          <dd className="col-sm-8">{form.gender || "-"}</dd>

                          <dt className="col-sm-4">Marital Status</dt>
                          <dd className="col-sm-8">{form.maritalStatus || "-"}</dd>

                          <dt className="col-sm-4">Phone</dt>
                          <dd className="col-sm-8">{form.phone || "-"}</dd>

                          <dt className="col-sm-4">Aadhaar No.</dt>
                          <dd className="col-sm-8">{form.aadhaarNumber || "-"}</dd>

                          <dt className="col-sm-4">Department</dt>
                          <dd className="col-sm-8">{form.department || "-"}</dd>

                          <dt className="col-sm-4">Role</dt>
                          <dd className="col-sm-8">{form.role || "-"}</dd>

                          <dt className="col-sm-4">Date of Joining</dt>
                          <dd className="col-sm-8">{form.joinedAt || "Not provided"}</dd>

                          <dt className="col-sm-4">Status</dt>
                          <dd className="col-sm-8">
                            <span className={`badge rounded-pill ${statusBadgeClass(form.status)}`}>
                              {form.status}
                            </span>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handlePrevious}
                  disabled={currentStep === 0 || isSubmitting}
                >
                  <i className="bi bi-arrow-left me-1" /> Previous
                </button>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={isSubmitting}
                    onClick={() => navigate("/admin/employees")}
                  >
                    Cancel
                  </button>

                  {currentStep < steps.length - 1 ? (
                    <button
                      type="button"
                      style={{ background: "linear-gradient(135deg, #d97706, #f59e0b)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 18px", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
                      onClick={handleNext}
                    >
                      Next Step <i className="bi bi-arrow-right" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      style={{ background: "linear-gradient(135deg, #d97706, #f59e0b)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 18px", fontSize: "0.875rem", fontWeight: 600, cursor: isSubmitting ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, opacity: isSubmitting ? 0.75 : 1 }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Saving...</>
                      ) : (
                        <><i className="bi bi-check2" /> Update Employee</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditEmployee;
